// src/components/Cards/UpcomingCard.tsx
import Link from "next/link";
import ImageWrapper from "../ImageWrapper";
import { dateFormat, formatPrice, isLaunchWithinOneMonth } from "@/app/utilities/helper"; // ← reuse same helper as ProductCard if possible
import LeadButton from "../Lead/LeadButton";
import { getLeadButtonTitle } from "@/lib/utils/leadButtonTitle";
import Image from "next/image";
import Heading from "../Heading";
import { HeadingProps } from "@/app/utilities/interfaces";

interface UpcomingCar {
	_id: string;
	name: string;               // or model
	slug: string;
	brand_name: string;
	brand_slug: string;
	featured_image?: string;
	price_range?: {
		base?: number;
		top?: number;
	};
	expected_price?: string;    // fallback string version e.g. "18 - 24 Lakh"
	launch_date?: string | null;   // e.g. "April 2028" or "Q2 2026"
	is_upcoming?: boolean;
	status?: string;
	fuel_types?: string[];
	specs_range?: { key: string; label?: string; unit?: string; min: number; max: number }[];
	leadsumbittracking?: string; // optional tracking class for lead form submission
}

interface UpcomingCardProps {
	car: UpcomingCar;
	href?: string;
	leadBtnText?: string;
	showBasePriceOnly?: boolean;
	index?: number;
	trackingClass?: string;
	cardClickTrackingClass?: string;
	leadsumbittracking?: string;
	footerLinkHref?: string;
	footerLinkLabel?: string;
	productStyleBottom?: boolean;
	headingTag?: HeadingProps["tag"];
}

const resolveTemplate = (template: string, car: any) => {
	if (!template) return "";
	return template
		.replace(/{brandName}/g, car.brand_name || "")
		.replace(/{brandSlug}/g, car.brand_slug || "")
		.replace(/{model}/g, car.name || "")
		.replace(/{modelSlug}/g, car.slug || "")
};

export default function UpcomingCard({
	car,
	href,
	leadBtnText = "Notify Me",
	showBasePriceOnly = true,
	index,
	trackingClass = '',
	cardClickTrackingClass = '',
	leadsumbittracking,
	footerLinkHref,
	footerLinkLabel,
	productStyleBottom,
	headingTag = "h3"
}: UpcomingCardProps) {
	const defaultHref = `/${car.brand_slug}/${car.slug}`;
	const linkHref = href || defaultHref;

	const imageUrl =
		car.featured_image ||
		"https://via.placeholder.com/600x360?text=No+Image";

	const title = `${car.brand_name} ${car.name}`;

	const basePrice = car.price_range?.base ? formatPrice(car.price_range.base, { includeUnit: true }) : null;
	const topPrice = car.price_range?.top ? formatPrice(car.price_range.top, { includeUnit: true }) : null;
	const priceDisplay = showBasePriceOnly || !topPrice || topPrice === basePrice ? basePrice || "Price on Request" : `${basePrice} - ${topPrice}`;

	const formattedLaunchDate = car.launch_date ? dateFormat(car.launch_date) : "Coming Soon";

	const finalTag = () => {
		if (isLaunchWithinOneMonth(car.launch_date)) {
			return {
				tag: "Launching Soon",
				textcolor: "text-[#B45309]",
				background: "bg-[#FEF3C7]",
				border: "border-[#F59E0B]"
			}
		}
		return {
			tag: "Upcoming",
			textcolor: "text-[#FD9C00]",
			background: "bg-[#FFF6E6]",
			border: "border-[#FD9C00]/60"
		}
	}

	const resolvedFooterLinkHref = footerLinkHref
		? (footerLinkHref.includes("{") ? resolveTemplate(footerLinkHref, car) : defaultHref + footerLinkHref)
		: "";

	return (
		<div className="bg-white border border-gray-200 rounded-[10px] md:rounded-[24px] overflow-hidden h-full flex flex-col">
			{/* Image + badge */}
			<Link prefetch={false} href={linkHref} className={`block relative ${cardClickTrackingClass}`} title={title}>
				<div className="w-full aspect-[11/6] bg-gray-50">
					<ImageWrapper
						src={imageUrl}
						alt={title}
						title={title}
						width={275}
						height={150}
						className="w-full h-full object-cover"
						priority={index !== undefined && index <= 3}
					/>
				</div>

				<span className={`absolute top-2 left-2 inline-flex items-center px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${finalTag().textcolor} ${finalTag().background} border ${finalTag().border} rounded-md`}>
					{finalTag().tag}
				</span>
			</Link>

			{/* Optional launch date banner */}
			{car.launch_date ? (
				<div className="bg-[#008C8B0D] px-4 py-1.5 text-center text-xs font-medium text-[#008C8B]">
					Expected Launch:{" "}
					<span className="font-semibold">{formattedLaunchDate}</span>
				</div>
			) : (
				<div className="bg-[#008C8B0D] px-4 py-1.5 text-center text-xs font-semibold text-[#008C8B]">
					Coming Soon
				</div>
			)}

			<div className="p-4 flex flex-col flex-grow">
				{!productStyleBottom ? (
					<div className="flex flex-row justify-between items-center pb-2">
						{/* Brand + Model */}
						<Link prefetch={false} href={linkHref} className={`block ${cardClickTrackingClass}`} title={title}>
							<div className="text-xs md:text-sm text-[#6A7282] capitalize tracking-wide">
								{car.brand_name}
							</div>
							<Heading tag={headingTag} size={headingTag} className="w-full md:w-[100px] !text-[14px] !font-semibold !line-clamp-1 mt-0.5">
								{car.name}
							</Heading>
						</Link>

						<div className="text-right">
							<div className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">Estimated Price</div>
							{priceDisplay && priceDisplay !== "Price on Request" ? (
								<p className="text-[16px] text-[#101828] mt-1 font-bold">{priceDisplay}<span className="text-[11px] text-[#62748E] font-normal ml-0.5"> onwards</span></p>
							) : (
								<p className='text-[12px] text-gray-700 uppercase tracking-wider font-bold mt-1'>
									To be Announced
								</p>
							)}
						</div>
					</div>
				):(
					<div className="">
						<Link prefetch={false} href={linkHref} className="block" title={title}>
							<Heading tag={headingTag} size={headingTag} className="!text-[15px] !font-semibold !text-[#0F172B] line-clamp-1 mt-0.5">
								{car.brand_name} {car.name}
							</Heading>
						</Link>
						{priceDisplay && priceDisplay !== "Price on Request" ? (
							<p className="text-[16px] text-[#101828] mt-1 font-bold">{priceDisplay}<span className="text-[11px] text-[#62748E] font-normal ml-0.5"> onwards</span></p>
						) : (
							<p className='text-[16px] leading-[24px] text-[#314158] tracking-wider font-bold mt-1'>
								Price To be Announced
							</p>
						)}
					</div>
				)}

				{/* CTA – same button style as ProductCard */}
				<div className="mt-auto pt-4">
					<LeadButton
						carId={car._id}
						carName={title}
						title={getLeadButtonTitle(title, leadBtnText)}
						datatestid="car-notify-cta-button"
						btnText={
							<span className="flex items-center justify-center gap-1.5 text-[14px]">
								{leadBtnText}
								<Image
									src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/ChevronRight.svg`}
									alt="ChevronRight Next"
									width={4}
									height={4}
									className="w-4 h-4 p-[2px]"
									title="ChevronRight Next"
									/>
							</span>
						}
						fullWidth
						className={`w-full py-2.5 text-base bg-[#008C8B] hover:opacity-90 text-white rounded-lg flex items-center justify-center transition font-medium ${trackingClass}`}
						is_upcoming={true}
						leadsumbittracking={leadsumbittracking}
					/>
				</div>

				{resolvedFooterLinkHref && footerLinkLabel && (
					<Link
						href={resolvedFooterLinkHref}
						title={resolveTemplate(footerLinkLabel, car)}
						prefetch={false}
						className="mt-3 text-[13px] font-medium text-blue-600 hover:text-blue-700 transition flex items-center justify-center"
					>
						{resolveTemplate(footerLinkLabel, car)}
					</Link>
				)}
			</div>
		</div >
	);
}